import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from '_common/hooks';

import { Tooltip, Divider, IconName, Icon, EmptyState } from '@ets/design-system';

import { useEffectOnUpdate } from '_common/hooks';
import { InstanceService, SharedService } from '_common/services';
import { updateModal } from '_common/modals/ModalsSlice';

import {
  ThunksTable,
  Cell,
  type TableColumn,
  type TableRow,
  type onRowClickHandler,
  type onDoubleClickHandler,
} from '_common/components/Table';

import Menu from './Menu/Menu';
import Header from './Header';
import { listObjects, setSelected } from '_common/components/Table/TableSlice';

import styles from './NavigateToDoDOC.module.scss';
import { getDocumentObject } from 'Editor/redux/EditorStatusSlice';

type NavigateToDoDOCProps = {
  menuOptions: {
    identity?: string;
    label?: string;
    icon: IconName;
  }[];
  menuSelected: {
    identity?: string;
    label?: string;
    icon: IconName;
  };
  title: string;
};

type RowData = Objekt & { disabled: boolean };

const NavigateToDoDOC = ({ menuOptions, menuSelected, title }: NavigateToDoDOCProps) => {
  const data = useSelector((state) => state.app.data);
  const intl = useIntl();
  const selected = Object.keys(useSelector((state) => state.table.selected));
  const object = data[selected[0]];
  const current = useSelector((state) => state.modals.CitationsModal.navigation?.current);
  const identity = useSelector((state) => state.modals.CitationsModal.navigation?.identity);
  const dispatch = useDispatch();
  const [parent, setParent] = useState<Objekt | null>(null);
  const [selectRow, setSelectRow] = useState<string | undefined>();
  const advancedFilters = useSelector((state) => state.search.advancedFilters);
  const document = useSelector(getDocumentObject);
  const list = useSelector((state) => state.table.identity[identity as Table.Identity]?.list || []);

  //#region Table object listing handlers
  const fetchObjects = useCallback(
    async (parameters: any) => {
      if (identity === 'storage' || identity === 'storage/folder') {
        const instanceService = new InstanceService();
        if (!object && !data[current]) {
          const { data } = await instanceService.getPersonalSpaceInfo();
          dispatch(
            updateModal({
              modal: 'CitationsModal',
              // @ts-expect-error Missing endpoint type "/api/object/space/get"
              data: { navigation: { current: data.id, identity: 'storage' } },
            }),
          );
          // @ts-expect-error Missing endpoint type "/api/object/space/get"
          setParent(data);
          // @ts-expect-error Missing endpoint type "/api/object/space/get"
          const resp = await instanceService.listObject(data.id, data.type, parameters);
          // @ts-expect-error Generic endpoint
          resp.data.nodes.map((node) => {
            if (node.id === document.id) {
              node.citations = document.citations;
              return node;
            } else {
              return node;
            }
          });
          return resp;
        } else {
          const selectedObject = data[current];
          dispatch(
            updateModal({
              modal: 'CitationsModal',
              data: { navigation: { current, identity: 'storage' } },
            }),
          );
          const resp = await instanceService.listObject(
            selectedObject.id,
            selectedObject.type,
            parameters,
          );
          // @ts-expect-error Generic endpoint
          resp.data.nodes.map((node) => {
            if (node.id === document.id) {
              node.citations = document.citations;
              return node;
            } else {
              return node;
            }
          });

          return resp;
        }
      } else if (identity === 'spaces') {
        const res = await new InstanceService().listSpaces(parameters);
        return res;
      } else if (identity === 'shared') {
        const resp = await new SharedService().listShared(parameters);
        // @ts-expect-error Missing endpoint type "/api/object/share/list"
        resp.data.nodes.map((node) => {
          if (node.id === document.id) {
            node.citations = document.citations;
            return node;
          } else {
            return node;
          }
        });
        return resp;
      } else {
        const resp = await new InstanceService().searchObject(
          [],
          parameters,
          advancedFilters.initialFilter?.query,
        );
        // @ts-expect-error Generic endpoint
        resp.data.nodes.map((node) => {
          if (node.id === document.id) {
            node.citations = document.citations;
            return node;
          } else {
            return node;
          }
        });

        return resp;
      }
    },
    [identity, current],
  );

  useEffect(() => {
    dispatch(
      listObjects({
        identity,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, []);

  useEffectOnUpdate(() => {
    dispatch(
      listObjects({
        identity,
        fetch: fetchObjects,
        cause: 'FILTER',
        request: { offset: 0 },
      }),
    );
  }, [fetchObjects]);
  //#endregion

  const onRowDoubleClick: onDoubleClickHandler<RowData> = (object) => {
    const currentObject = data[(object as RowData).id];

    if (currentObject.status === 'processing' || currentObject.status === 'broken') {
      return;
    }

    let type = currentObject.type;
    let id = currentObject.id;
    switch (type) {
      case 'space':
      case 'folder':
        dispatch(
          updateModal({
            modal: 'CitationsModal',
            data: {
              navigation: { current: id, identity: 'storage' },
            },
          }),
        );
        break;
      default:
        break;
    }
  };

  const onRowClick: onRowClickHandler<RowData> = (row) => {
    const { id } = row.original;
    const isDisabled =
      data[id].type !== 'folder' &&
      data[id].type !== 'space' &&
      data[id].mime?.type !== 'text/xml' &&
      data[id].mime?.type !== 'text/plain';
    if (!isDisabled) {
      setSelectRow(row.id);
      dispatch(setSelected({ [id]: true }));
    }
  };

  const columns = useMemo<TableColumn<RowData>[]>(() => {
    return [
      {
        id: 'name',
        accessorKey: 'name',
        enableSorting: false,
        minWidth: 296,
        header: intl.formatMessage({ id: 'global.name' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-name-column`} disabled={object.disabled}>
              <Cell.ObjectName object={object} testId={`${object.id}-object-name`} />
            </Cell>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableSorting: false,
        size: 136,
        header: intl.formatMessage({ id: 'STATUS' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-status-column`} disabled={object.disabled}>
              <span className={styles.colStatus}>
                <Cell.ObjectStatus object={object} testId="object-status" />
              </span>
            </Cell>
          );
        },
      },
    ];
  }, []);

  const rowsData = useMemo(() => {
    if (!list) {
      return [];
    }
    return list.map<TableRow<RowData>>((objectId) => {
      const object = data[objectId];
      const isDisabled =
        object.type !== 'folder' &&
        object.type !== 'space' &&
        object.mime?.type !== 'text/xml' &&
        object.mime?.type !== 'text/plain';

      return {
        ...object,
        disabled: isDisabled,
        suffix: isDisabled
          ? {
              children: (
                <Cell testId={`${objectId}-suffix-column`}>
                  <Tooltip
                    content={intl.formatMessage({ id: 'INVALID_FILE_FORMAT' })}
                    testId={`object-${objectId}-invalid-file-format`}
                  >
                    <span>
                      <Icon
                        icon="circle-info"
                        size="2"
                        testId={`object-${objectId}-invalid-file-format`}
                        color="primary"
                      />
                    </span>
                  </Tooltip>
                </Cell>
              ),
            }
          : undefined,
      };
    });
  }, [list, data]);

  const renderEmptyView = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
        <EmptyState
          size="l"
          icon="file"
          title={intl.formatMessage({ id: 'storage.browserHeader.spaceEmpty' })}
          testId="empty-folder"
          description={intl.formatMessage({ id: 'EMPTY_FOLDER' })}
        />
      </div>
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.title}>{title}</div>
      <div className={styles.container}>
        <Menu list={menuOptions} selected={menuSelected} />
        <Divider margin="0.25rem 0 0.25rem 0" vertical />
        <div>
          <Header data={data} current={current} identity={identity} parent={parent} />
          <div className={styles.list}>
            <ThunksTable
              identity={identity}
              onDoubleClick={onRowDoubleClick}
              onRowClick={onRowClick}
              selectable={false}
              options={{ columns, data: rowsData }}
              lazyLoading
              loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
              renderFooter={() => null}
              renderEmptyState={renderEmptyView}
              fetchObjects={fetchObjects}
              selection={{ [`${selectRow}`]: true }}
              testId="navigate-to-dodoc"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigateToDoDOC;
